import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { TemplateFieldControl } from '../../types';

interface SettingsControlProps<T> {
  label: string;
  control: TemplateFieldControl<T>;
  onChange: (next: TemplateFieldControl<T>) => void;
  /** Renders the default-value editor; receives current default + setter. */
  children: (value: T, setValue: (value: T) => void) => React.ReactNode;
  error?: string;
  /** Hide the Visible/Required toggles (field is backend-mandated for this type). */
  locked?: boolean;
}

/**
 * One row of a type-specific settings section: a default-value editor plus
 * Visible / Required toggles, bound to a single TemplateFieldControl.
 */
export function SettingsControl<T>({ label, control, onChange, children, error, locked }: SettingsControlProps<T>) {
  const setValue = (value: T) => onChange({ ...control, default: value });

  return (
    <Box mb={2} display="flex" gap={2} alignItems="flex-start">
      <Box flex={1}>
        <Typography variant="subtitle2" mb={0.5}>
          {label} {control.required && <span style={{ color: 'red' }}>*</span>}
        </Typography>
        {children(control.default, setValue)}
        {error && (
          <Typography variant="caption" color="error" role="alert">
            {error}
          </Typography>
        )}
      </Box>
      {/* Reserve the column width even when locked so every field lines up. */}
      <Box display="flex" flexDirection="column" flexShrink={0} minWidth={160}>
        {!locked && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={control.visible}
                  onChange={e => onChange({ ...control, visible: e.target.checked })}
                  inputProps={{ 'aria-label': `${label} Visible` }}
                />
              }
              label="Visible"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={control.required}
                  onChange={e => onChange({ ...control, required: e.target.checked })}
                  inputProps={{ 'aria-label': `${label} Required` }}
                />
              }
              label="Required"
            />
          </>
        )}
      </Box>
    </Box>
  );
}
